import React, { useState } from "react";
import { purchaseStorageAddon } from "../../apis/Profile/StorageApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../app/BusinessDash/components/ui/dialog";
import { Button } from "../../app/BusinessDash/components/ui/button";

const STORAGE_OPTIONS = [
  { size_gb: 5, price_dkk: 500 },
  { size_gb: 10, price_dkk: 900 },
  { size_gb: 50, price_dkk: 3500 },
];

const formatPrice = (price) =>
  typeof price === "number"
    ? `${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")},- DKK / year`
    : "-";

const StorageUpgradeModal = ({
  isOpen,
  onClose,
  isAccessManager,
  targetUserId = null,
  onRequestUpgrade,
}) => {
  const [selectedOption, setSelectedOption] = useState(STORAGE_OPTIONS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handlePurchase = async () => {
    if (!selectedOption) return;
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        size_gb: selectedOption.size_gb,
      };

      if (targetUserId) {
        payload.user_id = Number(targetUserId);
      }

      const response = await purchaseStorageAddon(payload);

      if (response?.sessionUrl) {
        window.location.href = response.sessionUrl;
        return;
      }

      setSuccessMessage(
        "Storage add-on activated successfully. Your limit will update shortly."
      );

      setTimeout(() => {
        setIsSubmitting(false);
        onClose?.();
        window.location.reload();
      }, 1200);
    } catch (err) {
      const message =
        err?.error ||
        err?.message ||
        "Failed to start storage purchase. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestUpgrade = async () => {
    if (typeof onRequestUpgrade === "function") {
      await onRequestUpgrade();
    }
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Data Storage</DialogTitle>
          <DialogDescription>
            Each Business Dash seat includes 1 GB. Purchase extra storage to
            continue uploading documents securely.
          </DialogDescription>
        </DialogHeader>

        {isAccessManager ? (
          <div className="space-y-4 mt-2">
            <div className="space-y-3">
              {STORAGE_OPTIONS.map((option) => (
                <button
                  key={option.size_gb}
                  type="button"
                  className={`w-full flex items-center justify-between border rounded-lg px-4 py-3 text-left transition-colors ${
                    selectedOption?.size_gb === option.size_gb
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-primary/40"
                  }`}
                  onClick={() => setSelectedOption(option)}
                  disabled={isSubmitting}
                >
                  <div>
                    <p className="font-medium text-sm">
                      +{option.size_gb} GB storage
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Billed annually
                    </p>
                  </div>
                  <span className="text-sm font-semibold">
                    {formatPrice(option.price_dkk)}
                  </span>
                </button>
              ))}
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
                {successMessage}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onClose?.()}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handlePurchase} disabled={isSubmitting}>
                {isSubmitting ? "Processing..." : "Continue to payment"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-3">
            <p className="text-sm text-muted-foreground">
              Reach out to your Access Manager to increase storage for the
              Business Dash. We can send them a notification on your behalf.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onClose?.()}>
                Close
              </Button>
              <Button onClick={handleRequestUpgrade}>
                Notify Access Manager
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StorageUpgradeModal;

